import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Grid,
  alpha,
  Tabs,
  Tab,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import LocalDrinkIcon from '@mui/icons-material/LocalDrink';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import TodayIcon from '@mui/icons-material/Today';
import HistoryIcon from '@mui/icons-material/History';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../services/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import PageLayout from './PageLayout';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ptBR from 'date-fns/locale/pt-BR';
import {
  format,
  startOfDay,
  subDays,
  startOfMonth,
  endOfMonth,
  isWithinInterval,
  parseISO,
} from 'date-fns';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';

const VITALITY_COLOR = '#2E7D32';

const MEAL_TYPES = [
  { id: 'cafe_manha', label: 'Café da manhã' },
  { id: 'almoco', label: 'Almoço' },
  { id: 'jantar', label: 'Jantar' },
  { id: 'lanche', label: 'Lanche' },
];

const DEFAULT_HYDRATION_GOAL = 2000;

function getDateKey(date) {
  return format(startOfDay(date), 'yyyy-MM-dd');
}

export default function Nutricao({ customColors }) {
  const { currentUser } = useAuth();
  const primaryColor = customColors?.primary || VITALITY_COLOR;

  const [activeTab, setActiveTab] = useState(0);
  const [viewMode, setViewMode] = useState('week'); // 'week' | 'month'

  const [hydrationEntries, setHydrationEntries] = useState([]);
  const [hydrationGoal, setHydrationGoal] = useState(DEFAULT_HYDRATION_GOAL);
  const [hydrationGoalInput, setHydrationGoalInput] = useState(DEFAULT_HYDRATION_GOAL.toString());

  const [mealEntries, setMealEntries] = useState([]);

  const [mealDate, setMealDate] = useState(new Date());
  const [mealTime, setMealTime] = useState('12:00');
  const [mealType, setMealType] = useState('almoco');
  const [mealDescription, setMealDescription] = useState('');
  const [mealQuality, setMealQuality] = useState(3);

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const load = async () => {
      if (!currentUser?.uid) return;
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const snap = await getDoc(userRef);
        if (snap.exists()) {
          const data = snap.data();
          const hEntries = (data.nutritionHydrationEntries || []).map((e) => ({
            ...e,
            date: e.date,
            totalMl: Number(e.totalMl) || 0,
          }));
          hEntries.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
          setHydrationEntries(hEntries);

          const goal = Number(data.nutritionHydrationGoal) || DEFAULT_HYDRATION_GOAL;
          setHydrationGoal(goal);
          setHydrationGoalInput(goal.toString());

          const mEntries = (data.nutritionMeals || []).map((m) => ({
            ...m,
            quality: Number(m.quality) || 0,
          }));
          mEntries.sort((a, b) => {
            const da = a.date || '';
            const dbv = b.date || '';
            if (da === dbv) {
              return (b.time || '').localeCompare(a.time || '');
            }
            return dbv.localeCompare(da);
          });
          setMealEntries(mEntries);
        }
      } catch (err) {
        console.error('Erro ao carregar dados de nutrição:', err);
      }
    };
    load();
  }, [currentUser?.uid]);

  const todayKey = getDateKey(new Date());

  const todayHydration = useMemo(() => {
    const entry = hydrationEntries.find((e) => e.date === todayKey);
    return entry?.totalMl || 0;
  }, [hydrationEntries, todayKey]);

  const todayMeals = useMemo(
    () => mealEntries.filter((m) => m.date === todayKey),
    [mealEntries, todayKey],
  );

  const todayNutritionScore = useMemo(() => {
    if (!todayMeals.length) return 0;
    const avg =
      todayMeals.reduce((sum, m) => sum + (Number(m.quality) || 0), 0) /
      todayMeals.length;
    return Math.round((avg / 5) * 100);
  }, [todayMeals]);

  const weeklyConsistency = useMemo(() => {
    const now = new Date();
    const last7 = Array.from({ length: 7 }).map((_, i) => getDateKey(subDays(now, i)));
    let daysWithRecords = 0;
    last7.forEach((key) => {
      const hasHydration = hydrationEntries.some((h) => h.date === key && h.totalMl > 0);
      const hasMeals = mealEntries.some((m) => m.date === key);
      if (hasHydration || hasMeals) daysWithRecords += 1;
    });
    return Math.round((daysWithRecords / 7) * 100);
  }, [hydrationEntries, mealEntries]);

  const daysRange = viewMode === 'week' ? 7 : 30;

  const hydrationChartData = useMemo(() => {
    const now = new Date();
    const days = Array.from({ length: daysRange })
      .map((_, i) => subDays(now, daysRange - 1 - i));
    return days.map((d) => {
      const key = getDateKey(d);
      const entry = hydrationEntries.find((h) => h.date === key);
      return {
        dateLabel: format(d, 'dd/MM', { locale: ptBR }),
        totalMl: entry?.totalMl || 0,
      };
    });
  }, [hydrationEntries, daysRange]);

  const mealsPerDayChartData = useMemo(() => {
    const now = new Date();
    const days = Array.from({ length: daysRange })
      .map((_, i) => subDays(now, daysRange - 1 - i));
    return days.map((d) => {
      const key = getDateKey(d);
      const count = mealEntries.filter((m) => m.date === key).length;
      return {
        dateLabel: format(d, 'dd/MM', { locale: ptBR }),
        count,
      };
    });
  }, [mealEntries, daysRange]);

  const filteredHydrationHistory = useMemo(() => {
    const now = new Date();
    const start = viewMode === 'week' ? subDays(now, 6) : startOfMonth(now);
    const end = now;
    return hydrationEntries.filter((h) => {
      if (!h.date) return false;
      const d = parseISO(h.date);
      return isWithinInterval(d, { start, end });
    });
  }, [hydrationEntries, viewMode]);

  const filteredMealHistory = useMemo(() => {
    const now = new Date();
    const start = viewMode === 'week' ? subDays(now, 6) : startOfMonth(now);
    const end = now;
    return mealEntries.filter((m) => {
      if (!m.date) return false;
      const d = parseISO(m.date);
      return isWithinInterval(d, { start, end });
    });
  }, [mealEntries, viewMode]);

  const handleSaveHydrationGoal = async () => {
    if (!currentUser?.uid) return;
    const value = Number(hydrationGoalInput);
    if (!value || value <= 0) {
      setSnackbar({
        open: true,
        message: 'Informe uma meta de hidratação válida.',
        severity: 'warning',
      });
      return;
    }
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, { nutritionHydrationGoal: value });
      setHydrationGoal(value);
      setSnackbar({
        open: true,
        message: 'Meta de hidratação atualizada.',
        severity: 'success',
      });
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: 'Erro ao salvar meta.',
        severity: 'error',
      });
    }
  };

  const handleAddWater = async (amount) => {
    if (!currentUser?.uid) return;
    setLoading(true);
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const snap = await getDoc(userRef);
      const data = snap.exists() ? snap.data() : {};
      const currentEntries = (data.nutritionHydrationEntries || []).map((e) => ({
        ...e,
        totalMl: Number(e.totalMl) || 0,
      }));
      const idx = currentEntries.findIndex((e) => e.date === todayKey);
      if (idx >= 0) {
        currentEntries[idx] = {
          ...currentEntries[idx],
          totalMl: currentEntries[idx].totalMl + amount,
          updatedAt: new Date().toISOString(),
        };
      } else {
        currentEntries.push({
          date: todayKey,
          totalMl: amount,
          updatedAt: new Date().toISOString(),
        });
      }
      currentEntries.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
      await updateDoc(userRef, { nutritionHydrationEntries: currentEntries });
      setHydrationEntries(currentEntries);
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: 'Erro ao registrar água.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHydrationDay = async (entry) => {
    if (!currentUser?.uid) return;
    if (!window.confirm('Remover registro de hidratação deste dia?')) return;
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const snap = await getDoc(userRef);
      const data = snap.exists() ? snap.data() : {};
      const currentEntries = data.nutritionHydrationEntries || [];
      const updated = currentEntries.filter((e) => e.date !== entry.date);
      await updateDoc(userRef, { nutritionHydrationEntries: updated });
      setHydrationEntries(
        updated
          .map((e) => ({ ...e, totalMl: Number(e.totalMl) || 0 }))
          .sort((a, b) => (b.date || '').localeCompare(a.date || '')),
      );
      setSnackbar({
        open: true,
        message: 'Registro de hidratação removido.',
        severity: 'success',
      });
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: 'Erro ao remover registro.',
        severity: 'error',
      });
    }
  };

  const handleSaveMeal = async () => {
    if (!currentUser?.uid) return;
    const dateKey = getDateKey(mealDate);
    if (!mealDescription.trim()) {
      setSnackbar({
        open: true,
        message: 'Descreva o que foi consumido.',
        severity: 'warning',
      });
      return;
    }
    setLoading(true);
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const snap = await getDoc(userRef);
      const data = snap.exists() ? snap.data() : {};
      const currentMeals = data.nutritionMeals || [];
      const newMeal = {
        id: `meal-${Date.now()}`,
        date: dateKey,
        time: mealTime,
        mealType,
        description: mealDescription.trim(),
        quality: Number(mealQuality) || 0,
        createdAt: new Date().toISOString(),
      };
      const updated = [...currentMeals, newMeal];
      await updateDoc(userRef, { nutritionMeals: updated });
      const mapped = updated
        .map((m) => ({ ...m, quality: Number(m.quality) || 0 }))
        .sort((a, b) => {
          const da = a.date || '';
          const dbv = b.date || '';
          if (da === dbv) {
            return (b.time || '').localeCompare(a.time || '');
          }
          return dbv.localeCompare(da);
        });
      setMealEntries(mapped);
      setMealDescription('');
      setMealQuality(3);
      setMealTime('12:00');
      setMealType('almoco');
      setMealDate(new Date());
      setSnackbar({
        open: true,
        message: 'Refeição registrada com sucesso!',
        severity: 'success',
      });
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: 'Erro ao salvar refeição.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMeal = async (meal) => {
    if (!currentUser?.uid) return;
    if (!window.confirm('Remover esta refeição?')) return;
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const snap = await getDoc(userRef);
      const data = snap.exists() ? snap.data() : {};
      const currentMeals = data.nutritionMeals || [];
      const updated = currentMeals.filter((m) => m.id !== meal.id);
      await updateDoc(userRef, { nutritionMeals: updated });
      setMealEntries(
        updated
          .map((m) => ({ ...m, quality: Number(m.quality) || 0 }))
          .sort((a, b) => {
            const da = a.date || '';
            const dbv = b.date || '';
            if (da === dbv) {
              return (b.time || '').localeCompare(a.time || '');
            }
            return dbv.localeCompare(da);
          }),
      );
      setSnackbar({
        open: true,
        message: 'Refeição removida.',
        severity: 'success',
      });
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: 'Erro ao remover refeição.',
        severity: 'error',
      });
    }
  };

  const hydrationPercent = Math.min(
    100,
    hydrationGoal ? Math.round((todayHydration / hydrationGoal) * 100) : 0,
  );

  return (
    <PageLayout title="Nutrição" customColors={customColors} hideAddButton>
      <Box sx={{ maxWidth: 1200, mx: 'auto', pb: 4 }}>
        <Card
          sx={{
            mb: 2,
            background: `linear-gradient(135deg, ${alpha(primaryColor, 0.12)} 0%, ${alpha(
              primaryColor,
              0.04,
            )} 100%)`,
            borderLeft: `4px solid ${primaryColor}`,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': { fontWeight: 600 },
              '& .Mui-selected': { color: primaryColor },
              '& .MuiTabs-indicator': { backgroundColor: primaryColor },
            }}
          >
            <Tab icon={<TodayIcon />} iconPosition="start" label="Dashboard" />
            <Tab icon={<LocalDrinkIcon />} iconPosition="start" label="Hidratação" />
            <Tab icon={<RestaurantIcon />} iconPosition="start" label="Refeições" />
            <Tab icon={<HistoryIcon />} iconPosition="start" label="Histórico" />
          </Tabs>
        </Card>

        {activeTab === 0 && (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6} sm={4} md={2.4}>
                <Card
                  sx={{
                    height: '100%',
                    borderTop: `3px solid ${primaryColor}`,
                    bgcolor: alpha(primaryColor, 0.06),
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <LocalDrinkIcon sx={{ color: primaryColor }} />
                      <Typography variant="caption" color="text.secondary">
                        Água hoje
                      </Typography>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: primaryColor }}>
                      {todayHydration} ml
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4} md={2.4}>
                <Card
                  sx={{
                    height: '100%',
                    borderTop: `3px solid ${primaryColor}`,
                    bgcolor: alpha(primaryColor, 0.06),
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <LocalDrinkIcon sx={{ color: primaryColor }} />
                      <Typography variant="caption" color="text.secondary">
                        Meta diária
                      </Typography>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: primaryColor }}>
                      {hydrationGoal} ml
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4} md={2.4}>
                <Card
                  sx={{
                    height: '100%',
                    borderTop: `3px solid ${primaryColor}`,
                    bgcolor: alpha(primaryColor, 0.06),
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <RestaurantIcon sx={{ color: primaryColor }} />
                      <Typography variant="caption" color="text.secondary">
                        Refeições hoje
                      </Typography>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: primaryColor }}>
                      {todayMeals.length}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4} md={2.4}>
                <Card
                  sx={{
                    height: '100%',
                    borderTop: `3px solid ${primaryColor}`,
                    bgcolor: alpha(primaryColor, 0.06),
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <WhatshotIcon sx={{ color: primaryColor }} />
                      <Typography variant="caption" color="text.secondary">
                        Score nutricional
                      </Typography>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: primaryColor }}>
                      {todayNutritionScore}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4} md={2.4}>
                <Card
                  sx={{
                    height: '100%',
                    borderTop: `3px solid ${primaryColor}`,
                    bgcolor: alpha(primaryColor, 0.06),
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <ShowChartIcon sx={{ color: primaryColor }} />
                      <Typography variant="caption" color="text.secondary">
                        Consistência semanal
                      </Typography>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: primaryColor }}>
                      {weeklyConsistency}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Card sx={{ mb: 2, borderRadius: 2, border: `1px solid ${alpha(primaryColor, 0.3)}` }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: primaryColor }}>
                    Progresso de hidratação de hoje
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {hydrationPercent}% ({todayHydration} / {hydrationGoal} ml)
                  </Typography>
                </Box>
                <Box
                  sx={{
                    position: 'relative',
                    height: 18,
                    borderRadius: 999,
                    overflow: 'hidden',
                    bgcolor: alpha(primaryColor, 0.08),
                  }}
                >
                  <LinearProgress
                    variant="determinate"
                    value={hydrationPercent}
                    sx={{
                      height: '100%',
                      borderRadius: 999,
                      '& .MuiLinearProgress-bar': {
                        background: `linear-gradient(90deg, ${primaryColor}, ${alpha(
                          primaryColor,
                          0.5,
                        )})`,
                        transition: 'transform 0.4s ease',
                      },
                    }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Falta {Math.max(hydrationGoal - todayHydration, 0)} ml para atingir a meta.
                </Typography>
              </CardContent>
            </Card>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1.5,
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: primaryColor }}>
                Visão dos últimos {daysRange} dias
              </Typography>
              <ToggleButtonGroup
                size="small"
                value={viewMode}
                exclusive
                onChange={(_, v) => v && setViewMode(v)}
              >
                <ToggleButton value="week">Semana</ToggleButton>
                <ToggleButton value="month">Mês</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card sx={{ borderLeft: `4px solid ${primaryColor}` }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: primaryColor }}>
                      Consumo de água (ml)
                    </Typography>
                    {!hydrationChartData.some((d) => d.totalMl > 0) ? (
                      <Typography variant="body2" color="text.secondary">
                        Registre água para visualizar o gráfico.
                      </Typography>
                    ) : (
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={hydrationChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="dateLabel" tick={{ fontSize: 11 }} />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="totalMl" fill={primaryColor} name="Água (ml)" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card sx={{ borderLeft: `4px solid ${primaryColor}` }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: primaryColor }}>
                      Refeições por dia
                    </Typography>
                    {!mealsPerDayChartData.some((d) => d.count > 0) ? (
                      <Typography variant="body2" color="text.secondary">
                        Registre refeições para visualizar o gráfico.
                      </Typography>
                    ) : (
                      <ResponsiveContainer width="100%" height={240}>
                        <LineChart data={mealsPerDayChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="dateLabel" tick={{ fontSize: 11 }} />
                          <YAxis allowDecimals={false} />
                          <Tooltip />
                          <Line
                            type="monotone"
                            dataKey="count"
                            stroke={primaryColor}
                            name="Refeições"
                            strokeWidth={2}
                            dot={{ r: 4 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {activeTab === 1 && (
          <Card sx={{ mt: 2, borderLeft: `4px solid ${primaryColor}` }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, color: primaryColor, mb: 2 }}>
                Hidratação
              </Typography>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Meta diária de água (ml)
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      type="number"
                      size="small"
                      value={hydrationGoalInput}
                      onChange={(e) => setHydrationGoalInput(e.target.value)}
                      sx={{ width: 120 }}
                      inputProps={{ min: 200, step: 100 }}
                    />
                    <Button variant="outlined" onClick={handleSaveHydrationGoal} disabled={loading}>
                      Salvar meta
                    </Button>
                  </Box>
                </Grid>
              </Grid>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Adição rápida de água
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                {[250, 300, 500, 1000].map((amount) => (
                  <Button
                    key={amount}
                    variant="contained"
                    startIcon={<LocalDrinkIcon />}
                    onClick={() => handleAddWater(amount)}
                    disabled={loading}
                    sx={{
                      bgcolor: primaryColor,
                      '&:hover': { bgcolor: primaryColor, opacity: 0.9 },
                      minWidth: 110,
                    }}
                  >
                    +{amount} ml
                  </Button>
                ))}
              </Box>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Progresso de hoje
              </Typography>
              <Card
                variant="outlined"
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  borderColor: alpha(primaryColor, 0.4),
                  bgcolor: alpha(primaryColor, 0.03),
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {todayHydration} / {hydrationGoal} ml
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {hydrationPercent}%
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      position: 'relative',
                      height: 18,
                      borderRadius: 999,
                      overflow: 'hidden',
                      bgcolor: alpha(primaryColor, 0.08),
                    }}
                  >
                    <LinearProgress
                      variant="determinate"
                      value={hydrationPercent}
                      sx={{
                        height: '100%',
                        borderRadius: 999,
                        '& .MuiLinearProgress-bar': {
                          background: `linear-gradient(90deg, ${primaryColor}, ${alpha(
                            primaryColor,
                            0.5,
                          )})`,
                          transition: 'transform 0.4s ease',
                        },
                      }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Falta {Math.max(hydrationGoal - todayHydration, 0)} ml para atingir a meta.
                  </Typography>
                </CardContent>
              </Card>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Histórico diário de hidratação ({viewMode === 'week' ? 'últimos 7 dias' : 'este mês'})
              </Typography>
              {filteredHydrationHistory.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhum registro no período selecionado.
                </Typography>
              ) : (
                <List dense>
                  {filteredHydrationHistory.map((entry) => (
                    <ListItem
                      key={entry.date}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          size="small"
                          color="error"
                          onClick={() => handleDeleteHydrationDay(entry)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={`${format(new Date(entry.date + 'T12:00:00'), 'dd/MM/yyyy', {
                          locale: ptBR,
                        })} — ${entry.totalMl} ml`}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 2 && (
          <Card sx={{ mt: 2, borderLeft: `4px solid ${primaryColor}` }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, color: primaryColor, mb: 2 }}>
                Registro de refeições
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                    <DatePicker
                      label="Data"
                      value={mealDate}
                      onChange={(d) => setMealDate(d || new Date())}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    label="Horário"
                    type="time"
                    fullWidth
                    value={mealTime}
                    onChange={(e) => setMealTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    select
                    fullWidth
                    label="Tipo de refeição"
                    value={mealType}
                    onChange={(e) => setMealType(e.target.value)}
                    SelectProps={{ native: true }}
                  >
                    {MEAL_TYPES.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.label}
                      </option>
                    ))}
                  </TextField>
                </Grid>
              </Grid>

              <TextField
                label="O que foi consumido"
                multiline
                minRows={3}
                fullWidth
                value={mealDescription}
                onChange={(e) => setMealDescription(e.target.value)}
                sx={{ mt: 2 }}
              />

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Qualidade da refeição
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {[1, 2, 3, 4, 5].map((value) => (
                    <Button
                      key={value}
                      size="small"
                      variant={mealQuality === value ? 'contained' : 'outlined'}
                      onClick={() => setMealQuality(value)}
                      sx={{
                        minWidth: 40,
                        bgcolor: mealQuality === value ? primaryColor : undefined,
                        borderColor: primaryColor,
                        color: mealQuality === value ? 'primary.contrastText' : primaryColor,
                        '&:hover': {
                          bgcolor:
                            mealQuality === value ? primaryColor : alpha(primaryColor, 0.08),
                        },
                      }}
                    >
                      {value}
                    </Button>
                  ))}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleSaveMeal}
                  disabled={loading}
                  sx={{ bgcolor: primaryColor }}
                >
                  {loading ? 'Salvando...' : 'Salvar refeição'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {activeTab === 3 && (
          <Card sx={{ mt: 2, borderLeft: `4px solid ${primaryColor}` }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: primaryColor }}>
                  Histórico de refeições
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  value={viewMode}
                  exclusive
                  onChange={(_, v) => v && setViewMode(v)}
                >
                  <ToggleButton value="week">Semana</ToggleButton>
                  <ToggleButton value="month">Mês</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {filteredMealHistory.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhum registro no período selecionado.
                </Typography>
              ) : (
                <List dense>
                  {filteredMealHistory.map((meal) => {
                    const dateLabel = meal.date
                      ? format(new Date(meal.date + 'T12:00:00'), 'dd/MM/yyyy', {
                          locale: ptBR,
                        })
                      : '';
                    const typeLabel =
                      MEAL_TYPES.find((t) => t.id === meal.mealType)?.label || meal.mealType;
                    return (
                      <ListItem
                        key={meal.id}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            size="small"
                            color="error"
                            onClick={() => handleDeleteMeal(meal)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        <ListItemText
                          primary={`${dateLabel} ${meal.time || ''} — ${typeLabel}`}
                          secondary={`Qualidade: ${meal.quality || '-'} · ${
                            meal.description || ''
                          }`}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
}

